import { Meta, Response, Role, Vehicle } from '@agilox/common';
import {
	DropdownDirective,
	DropdownModule,
	IconModule,
	InputComponent,
	InputModule,
	NotifyComponent,
	PillComponent,
	SpinnerComponent,
} from '@agilox/ui';
import {
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	input,
	model,
	output,
	signal,
	viewChild,
	WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormValueControl } from '@angular/forms/signals';
import {
	combineLatest,
	debounceTime,
	distinctUntilChanged,
	filter,
	map,
	Observable,
	startWith,
	switchMap,
	tap,
} from 'rxjs';
import { VehicleUnionSelectService } from './services/vehicle-union-select.service';
import { VehicleSelectOptionComponent } from '../shared/vehicle-select-option/vehicle-select-option.component';
import { AsyncPipe, NgClass } from '@angular/common';
import { UnionSelectOptionComponent } from '../shared/union-select-option/union-select-option.component';
import { TranslateModule } from '@ngx-translate/core';
import { VehicleSelectOptionPipe } from './pipes/vehicle-select-option.pipe';

@Component({
	selector: 'vehicle-union-select',
	templateUrl: './vehicle-union-select.component.html',
	providers: [VehicleUnionSelectService],
	imports: [
		VehicleSelectOptionComponent,
		SpinnerComponent,
		DropdownModule,
		NgClass,
		PillComponent,
		InputModule,
		NotifyComponent,
		UnionSelectOptionComponent,
		TranslateModule,
		AsyncPipe,
		VehicleSelectOptionPipe,
		IconModule,
	],
})
export class VehicleUnionSelectComponent implements FormValueControl<Vehicle[]> {
	private vehicleUnionSelectService = inject(VehicleUnionSelectService);

	selectedSerials = input<string[]>([]);
	currentSelectedUnion = input<string | undefined>(undefined);
	userRole = input<Role>(Role.support);
	maxSelections = input<number>(20);
	maxSelectedVehiclesText = input<string>('select.maxSelectionsText');
	displayPills = input<boolean>(true);
	displayVehicleModel = input<boolean>(true);
	displayDeselectAll = input<boolean>(true);

	/**
	 * Grows the dropdown to the fullWidth of the largest option
	 */
	fullWidth = input<boolean>(true);

	disabled = input<boolean>(false);
	invalid = input<boolean>(false);
	touched = input<boolean>(false);

	value = model<Vehicle[]>([]);

	touch = output<void>();
	vehicleSelected = output<Vehicle[]>();

	searchInput = viewChild<InputComponent>('searchInput');

	dropdown = viewChild<DropdownDirective>(DropdownDirective);

	optionsList = viewChild<ElementRef<HTMLUListElement>>('optionsList');

	isInternalUser = computed(() => {
		// special case => partner is treated as an internal user
		return this.userRole() !== Role.customer;
	});

	isDisabledStyle = computed(
		() =>
			this.maxSelections() < this.selectedVehicles().length ||
			this.selectedVehicles().length === 0 ||
			!this.canSave()
	);

	private paginationMeta = computed(() => this.page());

	hasMorePages = computed(() => {
		const currentPage = this.page();
		return currentPage.size < (currentPage.total || 0);
	});

	currentSelectedUnionUuid = computed(() => this._currentSelectedUnion());

	public searchOpen = computed(() => this.dropdownOpen());

	selectedVehicles = computed(() => this._selectedVehicles() || []);

	canSave = computed(() => {
		const selectedCount = this._selectedVehicles().length;
		return selectedCount > 0 && selectedCount <= this.maxSelections();
	});

	selectedCount = computed(() => this._selectedVehicles().length);

	hasExceededMaxSelections = computed(() => this.maxSelections() < this.selectedCount());

	isDisabled = computed(() => this.disabled());

	searchText = signal<string>('');

	private readonly pageSize = 50;

	private searchObservable$ = toObservable(this.searchText).pipe(
		startWith(''),
		takeUntilDestroyed(),
		distinctUntilChanged(),
		tap(() => this.loadingVehicles.set(true))
	);

	/**
	 * If a user selects "Select all from union"
	 * We need to fetch all the vehicles from the union
	 * before we can select them
	 * @private
	 */
	private cachedUnionToSelect: string | undefined;

	private page = signal<Meta>({ number: 0, size: this.pageSize });
	private currentPage = signal<number>(1);

	public loadingVehicles: WritableSignal<boolean> = signal(false);

	private metaComparator = (prev: Meta, curr: Meta): boolean => {
		return prev.number === curr.number && prev.size === curr.size;
	};

	private _currentSelectedUnion = signal<string | undefined>('');

	/**
	 * Used in the template to trigger the pipe
	 * that creates / disables the options
	 */
	public currentSelectedUnion$ = toObservable(this._currentSelectedUnion).pipe(
		distinctUntilChanged()
	);

	/**
	 * Used to trigger the fetch of the vehicles
	 * We do not need to fetch the vehicles, if the currentSelectedUnion
	 * gets set to undefined. They only need to be fetched, if the union changes
	 */
	public selectedUnionChanged$ = toObservable(this._currentSelectedUnion).pipe(
		filter((union) => !!union),
		startWith(''),
		tap(() => {
			this.loadingVehicles.set(true);
			this.searchText.set('');
			this.resetPagination();
		})
	);

	private _initialLoad: boolean = true;

	public vehicleResponse$: Observable<Vehicle[]> = combineLatest([
		this.searchObservable$,
		toObservable(this.paginationMeta).pipe(distinctUntilChanged(this.metaComparator)),
		this.selectedUnionChanged$,
	]).pipe(
		debounceTime(300),
		switchMap(([search, page, union]: [string, Meta, string | undefined]) =>
			this.vehicleUnionSelectService
				.fetchVehicles(search, page, union || '', this.selectedSerials())
				.pipe(map((response) => this.sortOptions(response)))
		),
		tap((data) => {
			/**
			 * Only take the total from the response — adopting the full response meta
			 * would feed its size/totalPages back into the next request's page params.
			 */
			this.page.update((current) => ({ ...current, total: data.meta?.total }));
			this._loadedVehicles.set(data.data);
			if (this._initialLoad) {
				this._initialLoad = false;
				this.setVehiclesToSelectedVehiclesFromSerialStrings(
					this.selectedSerials(),
					data.data,
					data.meta
				);
				this.loadingVehicles.set(false);
			} else if (this.cachedUnionToSelect) {
				this.setVehiclesFromCachedUnion(data.data);
				this.loadingVehicles.set(false);
			} else {
				this.loadingVehicles.set(false);
			}
		}),
		map((data) => data.data)
	);

	public dropdownOpen = signal<boolean>(false);

	/**
	 * We do not want to immediately to write the vehicle to the form control
	 * but it should be triggered by the save button
	 * @private
	 */
	private _selectedVehicles = signal<Vehicle[]>([]);

	/**
	 * The vehicles from the latest response, used by "select all in union" to
	 * select the current union's vehicles without awaiting another fetch.
	 * @private
	 */
	private _loadedVehicles = signal<Vehicle[]>([]);

	constructor() {
		effect(() => {
			this._currentSelectedUnion.set(this.currentSelectedUnion());
		});
	}

	onScroll() {
		if (this.optionsList()) {
			const element = this.optionsList()!.nativeElement;
			const atBottom = element.scrollHeight - element.scrollTop === element.clientHeight;
			if (atBottom) {
				this.nextPage();
			}
		}
	}

	onSelect(vehicle: Vehicle) {
		/**
		 * A selected vehicle always toggles off, independent of the current union.
		 * This must come before any union check: vehicles without a union have no
		 * unionUuid, so gating deselection on a selected union would make them
		 * impossible to deselect. Clearing the last vehicle also releases the union.
		 */
		if (this._selectedVehicles().some((fcVehicle: Vehicle) => fcVehicle.uuid === vehicle.uuid)) {
			this._selectedVehicles.update((vehicles) => vehicles.filter((v) => v.uuid !== vehicle.uuid));
			if (this._selectedVehicles().length === 0) {
				this._currentSelectedUnion.set(undefined);
			}
			return;
		}

		if (this.currentSelectedUnionUuid() === vehicle.unionUuid) {
			/**
			 * Same union as the current selection (including the no-union group) →
			 * add it to the selection.
			 */
			this._selectedVehicles.update((vehicles) => [...vehicles, vehicle]);
		} else {
			/**
			 * If the union is different, we need to clear the previous selection and add the new one
			 * This should not be possible because we are disabling the checkboxes from different unions
			 * but this is a safety check
			 */
			this._currentSelectedUnion.set(vehicle.unionUuid);
			this._selectedVehicles.set([vehicle]);

			/**
			 * We then need to trigger a new backend call to get all the vehicles from the new union
			 */

			this.scrollToTop();
		}
	}

	private nextPage() {
		if (!this.hasMorePages()) {
			return;
		}

		this.currentPage.update((page) => page + 1);
		const newSize = this.pageSize * this.currentPage();

		this.page.update((currentPage) => ({
			...currentPage,
			size: newSize,
		}));
	}

	public isVehicleSelected(vehicle: Vehicle | undefined): boolean {
		return !!this.selectedVehicles().find((v) => v.uuid === vehicle?.uuid);
	}

	public onDeselectAll() {
		this._selectedVehicles.set([]);
		this._currentSelectedUnion.set(undefined);
	}

	public onSelectAllInUnion(uid: string) {
		this.cachedUnionToSelect = uid;

		/**
		 * The "select all" header only shows for the already-selected union, so the
		 * union rarely changes here — setting the same value would not re-trigger a
		 * fetch. Its vehicles are already loaded, so select them straight away.
		 * Only when the union actually changes (e.g. picking a whole union while
		 * none is selected yet) do we fetch it first and select in the response tap.
		 */
		if (this._currentSelectedUnion() === uid) {
			this.setVehiclesFromCachedUnion(this._loadedVehicles());
			return;
		}

		this._currentSelectedUnion.set(uid);
		// clear the search
		this.searchText.set('');
		// Reset pagination
		this.resetPagination();
	}

	private resetPagination() {
		this.currentPage.set(1);
		this.page.set({ number: 0, size: 50 });
	}

	private setVehiclesFromCachedUnion(vehicles: Vehicle[]) {
		this._selectedVehicles.set(
			vehicles.filter((vehicle) => vehicle.unionUuid === this.cachedUnionToSelect)
		);

		this.cachedUnionToSelect = undefined;
	}

	public onSave() {
		if (!this.commitSelection()) {
			return;
		}

		this.touch.emit();
		this.dropdown()?.closeDropdown();
	}

	private commitSelection(): boolean {
		if (!this.canSave()) {
			return false;
		}

		const vehicles = this._selectedVehicles();
		this.value.set(vehicles);
		this.vehicleSelected.emit(vehicles);
		return true;
	}

	onDropdownStateChange(open: boolean) {
		this.dropdownOpen.set(open);
		if (open) {
			this._selectedVehicles.set(this.value() || []);
			this.searchText.set('');
			this._currentSelectedUnion.set(this._selectedVehicles()[0]?.unionUuid || undefined);
			this.focusSearchInput();
		}
	}

	focusSearchInput() {
		this.clearAndFocusSearch();
	}

	clearAndFocusSearch() {
		this.searchText.set('');
		setTimeout(() => {
			this.searchInput()?.setFocus();
		}, 100);
	}

	/**
	 * Restores the selection from serials in the query params by matching them
	 * against the vehicles returned by the backend.
	 */
	private setVehiclesToSelectedVehiclesFromSerialStrings(
		serials: string[],
		vehicles: Vehicle[],
		meta?: Meta
	): void {
		if (!serials?.length || !vehicles?.length) {
			return;
		}

		const matchedVehicles = vehicles.filter((vehicle) => serials.includes(vehicle.serial));
		if (!matchedVehicles.length) {
			const listIsComplete = (meta?.total ?? 0) <= vehicles.length;
			if (listIsComplete) {
				this.onDeselectAll();
				this.value.set([]);
				this.vehicleSelected.emit([]);
			}
			return;
		}

		const firstUnion = matchedVehicles[0]?.unionUuid;
		const unionVehicles = matchedVehicles.filter((vehicle) => vehicle.unionUuid === firstUnion);

		const vehiclesBySerial = new Map<string, Vehicle>();
		unionVehicles.forEach((vehicle) => {
			if (!vehiclesBySerial.has(vehicle.serial)) {
				vehiclesBySerial.set(vehicle.serial, vehicle);
			}
		});

		this._currentSelectedUnion.set(firstUnion);
		this._selectedVehicles.set([...vehiclesBySerial.values()]);
		this.commitSelection();
	}

	private scrollToTop() {
		setTimeout(() => {
			const unionHeader: HTMLElement | null | undefined =
				this.optionsList()?.nativeElement.querySelector('#selected-union-header');
			if (unionHeader) {
				unionHeader.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		});
	}

	public toggleDropdown() {
		setTimeout(() => this.dropdown()?.toggleDropdown());
	}

	private sortOptions(response: Response<Vehicle>): Response<Vehicle> {
		const selectedSerials = this.selectedSerials() || [];
		const data = response.data ?? [];
		const selectedVehicles = data.filter((vehicle) => selectedSerials.includes(vehicle.serial));
		const unselectedVehicles = data.filter((vehicle) => !selectedSerials.includes(vehicle.serial));
		return {
			...response,
			data: [...selectedVehicles, ...unselectedVehicles],
		};
	}
}
